// Date-range picker modeled on Lightspeed's UI. Editorial black/white theme.
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "../lib/api";

const UNITS = ["Year", "Quarter", "Month", "Week", "Day"];

// Anchor date = latest data date (fetched once, shared). Falls back to the
// wall clock until it loads. Keeps "previous complete weeks" relative to the
// data we actually have, so a partial trailing week never skews comparisons.
let _anchorPromise = null;
function fetchAnchor() {
  if (!_anchorPromise) {
    _anchorPromise = api
      .get("/meta/data-through")
      .then((r) => r.data?.data_through || null)
      .catch(() => null);
  }
  return _anchorPromise;
}

function fmt(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function iso(d) {
  return d.toISOString().slice(0, 10);
}
function parseIso(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) { const x = startOfDay(d); return addDays(x, -((x.getDay() + 6) % 7)); }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfQuarter(d) { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3, 1); }
function startOfYear(d) { return new Date(d.getFullYear(), 0, 1); }

function unitStart(unit, d) {
  switch (unit) {
    case "Year": return startOfYear(d);
    case "Quarter": return startOfQuarter(d);
    case "Month": return startOfMonth(d);
    case "Week": return startOfWeek(d);
    default: return startOfDay(d);
  }
}
function addUnits(unit, d, n) {
  const x = new Date(d);
  switch (unit) {
    case "Year": x.setFullYear(x.getFullYear() + n); return x;
    case "Quarter": x.setMonth(x.getMonth() + n * 3); return x;
    case "Month": x.setMonth(x.getMonth() + n); return x;
    case "Week": return addDays(x, n * 7);
    default: return addDays(x, n);
  }
}

function computeRange(state, anchor) {
  const today = anchor ? startOfDay(parseIso(anchor)) : startOfDay(new Date());
  if (state.tab === "date") {
    return { start: parseIso(state.startDate), end: parseIso(state.endDate) };
  }
  const { unit, mode, n } = state;
  if (mode === "ytd_calendar") return { start: startOfYear(today), end: today };
  if (mode === "ytd_fiscal") {
    // Fiscal year starts Aug 1. If today is before Aug 1 this calendar year, use last year's Aug 1.
    const y = today.getFullYear();
    const thisFiscalStart = new Date(y, 7, 1); // month 7 = Aug
    const start = today >= thisFiscalStart ? thisFiscalStart : new Date(y - 1, 7, 1);
    return { start, end: today };
  }
  if (mode === "to_date") return { start: unitStart(unit, today), end: today };
  if (mode === "previous") {
    const currStart = unitStart(unit, today);
    return { start: addUnits(unit, currStart, -1), end: addDays(currStart, -1) };
  }
  // previous_n: last N COMPLETED units (excludes the current in-progress one)
  const currStart = unitStart(unit, today);
  return { start: addUnits(unit, currStart, -n), end: addDays(currStart, -1) };
}

function comparisonRange(range) {
  const days = Math.round((range.end - range.start) / 86400000) + 1;
  const compareEnd = addDays(range.start, -1);
  return { start: addDays(compareEnd, -(days - 1)), end: compareEnd };
}

const DEFAULT_STATE = { tab: "period", unit: "Month", mode: "previous_n", n: 3, startDate: iso(new Date()), endDate: iso(new Date()) };

export default function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(value?.state || DEFAULT_STATE);
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);

  useEffect(() => { fetchAnchor().then(setAnchor); }, []);

  // Allow external control (e.g. loading a saved view)
  useEffect(() => {
    if (value?.state) setState(value.state);
  }, [value?.stateKey]);

  const range = useMemo(() => computeRange(state, anchor), [state, anchor]);
  const compare = useMemo(() => comparisonRange(range), [range]);
  const label = `${fmt(range.start)} → ${fmt(range.end)}`;
  const compareLabel = `${fmt(compare.start)} → ${fmt(compare.end)}`;

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function apply(applyState = state) {
    const r = computeRange(applyState, anchor);
    const c = comparisonRange(r);
    onChange?.({
      state: applyState,
      start: iso(r.start),
      end: iso(r.end),
      compareStart: iso(c.start),
      compareEnd: iso(c.end),
      label: `${fmt(r.start)} → ${fmt(r.end)}`,
    });
    setOpen(false);
  }

  // Emit on mount and whenever the data anchor resolves (so the first paint
  // corrects itself from wall-clock to data-relative once loaded).
  useEffect(() => { apply(); /* eslint-disable-next-line */ }, [value?.stateKey, anchor]);

  const dateValid = state.tab !== "date" || (state.startDate && state.endDate && parseIso(state.startDate) <= parseIso(state.endDate));

  return (
    <div className="relative" ref={ref} data-testid="date-range-picker">
      <div>
        <div className="bm-eyebrow mb-2">Date range</div>
        <button
          data-testid="date-range-trigger"
          onClick={() => setOpen((v) => !v)}
          className="px-3 py-2 border border-[#0a0a0a] font-mono-bm text-sm flex items-center gap-2 hover:bg-[#0a0a0a]/5"
        >
          {label}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div data-testid="date-picker-popover" className="absolute z-50 mt-2 w-[480px] bg-[#f5f2eb] border border-[#0a0a0a] shadow-[8px_8px_0_0_#0a0a0a] right-0 bm-popover-mobile">
          {/* Tab bar */}
          <div className="flex border-b border-[#0a0a0a]">
            <button
              data-testid="tab-period"
              onClick={() => setState((s) => ({ ...s, tab: "period" }))}
              className={`flex-1 py-3 bm-eyebrow border-r border-[#0a0a0a] ${state.tab === "period" ? "bg-[#c72c2c] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >
              Specify period
            </button>
            <button
              data-testid="tab-date"
              onClick={() => setState((s) => ({ ...s, tab: "date", startDate: s.startDate || iso(range.start), endDate: s.endDate || iso(range.end) }))}
              className={`flex-1 py-3 bm-eyebrow ${state.tab === "date" ? "bg-[#c72c2c] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
            >
              Specify date
            </button>
          </div>

          {state.tab === "period" ? (
            <>
              <div className="flex border-b border-[#0a0a0a]">
                {UNITS.map((u) => (
                  <button
                    key={u}
                    data-testid={`unit-${u.toLowerCase()}`}
                    onClick={() => setState((s) => ({ ...s, unit: u, mode: s.mode === "ytd_calendar" || s.mode === "ytd_fiscal" ? "previous_n" : s.mode }))}
                    className={`flex-1 py-3 bm-eyebrow border-r border-[#0a0a0a] last:border-r-0 ${state.unit === u && state.mode !== "ytd_calendar" && state.mode !== "ytd_fiscal" ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/5"}`}
                  >
                    {u}
                  </button>
                ))}
              </div>
              <div className="p-5 space-y-4">
                <Option testId="mode-to-date" checked={state.mode === "to_date"} onClick={() => setState((s) => ({ ...s, mode: "to_date" }))} label={`${state.unit} to date`} />
                <Option testId="mode-previous" checked={state.mode === "previous"} onClick={() => setState((s) => ({ ...s, mode: "previous" }))} label={`Previous complete ${state.unit.toLowerCase()}`} />
                <div>
                  <Option testId="mode-previous-n" checked={state.mode === "previous_n"} onClick={() => setState((s) => ({ ...s, mode: "previous_n" }))} label={`Choose previous complete ${state.unit.toLowerCase()}s`} />
                  {state.mode === "previous_n" && (
                    <div className="mt-2 ml-7">
                      <div className="bm-eyebrow mb-1 text-neutral-600">Number of {state.unit.toLowerCase()}s</div>
                      <input
                        data-testid="previous-n-input"
                        type="number"
                        min="1"
                        max="60"
                        value={state.n}
                        onChange={(e) => setState((s) => ({ ...s, n: Math.max(1, parseInt(e.target.value) || 1) }))}
                        className="w-24 border border-[#0a0a0a] bg-transparent px-3 py-2 font-mono-bm text-sm focus:outline-none focus:border-[#c72c2c]"
                      />
                    </div>
                  )}
                </div>
                <div className="pt-3 border-t border-[#0a0a0a]/30 space-y-3">
                  <Option testId="mode-ytd-calendar" checked={state.mode === "ytd_calendar"} onClick={() => setState((s) => ({ ...s, mode: "ytd_calendar" }))} label="Calendar year-to-date" />
                  <Option testId="mode-ytd-fiscal" checked={state.mode === "ytd_fiscal"} onClick={() => setState((s) => ({ ...s, mode: "ytd_fiscal" }))} label="Fiscal year-to-date (Aug 1)" />
                </div>
              </div>
            </>
          ) : (
            <div className="p-5 space-y-4">
              <div>
                <label className="bm-eyebrow block mb-2">Start date</label>
                <input
                  data-testid="specify-start-date"
                  type="date"
                  value={state.startDate}
                  max={state.endDate}
                  onChange={(e) => setState((s) => ({ ...s, startDate: e.target.value }))}
                  className="w-full border border-[#0a0a0a] bg-transparent px-3 py-2 font-mono-bm text-sm focus:outline-none focus:border-[#c72c2c]"
                />
              </div>
              <div>
                <label className="bm-eyebrow block mb-2">End date</label>
                <input
                  data-testid="specify-end-date"
                  type="date"
                  value={state.endDate}
                  min={state.startDate}
                  onChange={(e) => setState((s) => ({ ...s, endDate: e.target.value }))}
                  className="w-full border border-[#0a0a0a] bg-transparent px-3 py-2 font-mono-bm text-sm focus:outline-none focus:border-[#c72c2c]"
                />
              </div>
              {!dateValid && <div className="text-xs text-[#c72c2c] font-mono-bm">End date must be on or after start date.</div>}
            </div>
          )}

          <div className="border-t border-[#0a0a0a] px-5 py-3 flex items-center justify-between gap-3">
            <div className="font-mono-bm text-xs text-neutral-700 truncate">{label}</div>
            <button
              data-testid="date-picker-apply"
              onClick={() => apply()}
              disabled={!dateValid}
              className="bm-eyebrow px-4 py-2 bg-[#0a0a0a] text-[#f5f2eb] hover:bg-[#c72c2c] disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Option({ checked, onClick, label, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} className="flex items-center gap-3 w-full text-left">
      <span className={`w-4 h-4 rounded-full border-2 border-[#0a0a0a] flex items-center justify-center flex-shrink-0 ${checked ? "bg-[#c72c2c] border-[#c72c2c]" : "bg-transparent"}`}>
        {checked && <span className="w-1.5 h-1.5 rounded-full bg-[#f5f2eb]" />}
      </span>
      <span className="text-sm">{label}</span>
    </button>
  );
}
